import angular from 'angular';
import * as hash from 'object-hash';

export const RecruiterCandidatesJobCacheService = (
    $cacheFactory,
    TalentService,
    PositionService,
    WorkflowService,
    RecruiterService,
    ImpersonationService,
    TalentScoreService,
    CACHE_CONSTS,
) => {
    const feedbackDataCacheKey = 'feedbackData';
    const feedbackDataCache = $cacheFactory.get(feedbackDataCacheKey) ?
        $cacheFactory.get(feedbackDataCacheKey) : $cacheFactory(feedbackDataCacheKey);

    let cacheKey = null;
    let cacheCreateTimestamp = null;

    const init = (controllerStateParams) => {
        const newCacheKey = hash.MD5({ ...controllerStateParams, talentID: null });
        const isCacheOutdated = Date.now() - cacheCreateTimestamp > CACHE_CONSTS.CACACHE_MAX_AGE_MS;
        if (newCacheKey !== cacheKey || isCacheOutdated) {
            cacheCreateTimestamp = Date.now();
            cacheKey = newCacheKey;
            clearCache();
        }
    };

    const clearCache = () => feedbackDataCache.removeAll();

    const getData = async ({ fetchDataFunction }) => fetchDataFunction();

    const getCustomData = async ({ positionId }) => {
        const [
            { result: positionData },
            { result: allWorkflows },
            { result: recruiters },
            { result: currentRecruiter },
            { result: selectedSamples },
        ] = await Promise.all([
            getPosition({ positionId, useCache: false }),
            getWorkflowSearch({ positionId }),
            getRecruiters({ projection: { positions: 0 }}),
            getBehalfOfOrCurrentTalentExpertRoleData({ useCache: false }),
            getSelectedSamples({ positionId }),
        ]);
        return { positionData, allWorkflows, recruiters, currentRecruiter, selectedSamples };
    };

    const getPosition = async ({ positionId, useCache }) => getData({
        dataKey: 'positionData',
        useCache,
        fetchDataFunction: () => PositionService.getPosition(positionId),
    });

    const getWorkflowSearch = async ({ positionId, useCache }) => getData({
        dataKey: 'allWorkflows',
        useCache,
        fetchDataFunction: () => WorkflowService.getWorkflowSearch({ positionId }),
    });

    const getRecruiters = async ({ projection = [], useCache } = {}) => getData({
        dataKey: 'recruiters',
        useCache,
        fetchDataFunction: () => RecruiterService.getRecruiters(projection),
    });

    const getBehalfOfOrCurrentTalentExpertRoleData = async ({ useCache } = {}) => getData({
        dataKey: 'currentRecruiter',
        useCache,
        fetchDataFunction: async () => ({ result: await ImpersonationService.getBehalfOfOrCurrentTalentExpertRoleData() }),
    });

    const getSelectedSamples = async ({ positionId, useCache }) => getData({
        dataKey: 'selectedSamples',
        useCache,
        fetchDataFunction: async () => ({ result: await PositionService.getSelectedSamples(positionId) }),
    });

    const postTalents = async ({ params, useCache }) => getData({
        dataKey: 'talents',
        useCache,
        fetchDataFunction: () => TalentService.postTalents(params),
    });

    const postTalentExplainScore = async ({ params, useCache }) => getData({
        dataKey: 'talentsExplain',
        useCache,
        fetchDataFunction: () => TalentScoreService.postTalentExplainScore(params),
    });

    const getSelectedAndManuallySamples = async ({ positionId, options, useCache }) => getData({
        dataKey: 'manuallyAddedSamples',
        useCache,
        fetchDataFunction: async () => ({ result: await PositionService.getSelectedAndManuallySamples(positionId, options) }),
    });

    const getSelectedAndFeedbackAndManuallyAddedSamples = async ({ positionId, options, useCache }) => getData({
        dataKey: 'feedbackAndManuallyAddedSamples',
        useCache,
        fetchDataFunction: async () => ({ result: await PositionService.getSelectedAndFeedbackAndManuallyAddedSamples(positionId, options) }),
    });

    return {
        init,
        getCustomData,
        getPosition,
        getWorkflowSearch,
        getRecruiters,
        getBehalfOfOrCurrentTalentExpertRoleData,
        getSelectedSamples,
        postTalents,
        postTalentExplainScore,
        getSelectedAndManuallySamples,
        getSelectedAndFeedbackAndManuallyAddedSamples,
    };
};

angular
    .module('TALENYA-FE')
    .factory('RecruiterCandidatesJobCacheService', RecruiterCandidatesJobCacheService);
